// Lightweight inline-SVG / CSS charts using RT design tokens. Accessible: every
// series carries its label + value as text, never color alone.

const barTone: Record<string, string> = {
  accent: "bg-accent",
  verified: "bg-verified",
  info: "bg-info",
  danger: "bg-danger",
  muted: "bg-muted",
};

/** Semantic tone for a marketplace status (shared with StatusLabel). */
export function statusTone(status: string): keyof typeof barTone {
  const s = status.toLowerCase();
  if (/accept|award|shortlist|offer|verified|active/.test(s)) return "verified";
  if (/appl|submit|review|view|open|pending/.test(s)) return "info";
  if (/reject|decline|not selected|fail/.test(s)) return "danger";
  return "muted";
}

/** Horizontal labeled bars (label + bar + value). */
export function BarBreakdown({ items }: { items: { label: string; value: number; tone?: keyof typeof barTone }[] }) {
  if (items.length === 0) return <p className="font-mono text-sm text-muted">No data yet.</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="grid gap-2.5">
      {items.map((i) => (
        <div key={i.label} className="grid gap-1">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono uppercase tracking-wide text-muted">{i.label}</span>
            <span className="font-mono font-bold text-ink">{i.value}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-2">
            <div className={`h-2 rounded-full ${barTone[i.tone ?? "accent"]}`} style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Small area chart of counts over ordered buckets. */
export function TrendArea({ points }: { points: { label: string; count: number }[] }) {
  const w = 320, h = 90, pad = 4;
  const max = Math.max(1, ...points.map((p) => p.count));
  const step = points.length > 1 ? (w - 2 * pad) / (points.length - 1) : 0;
  const xy = points.map((p, i) => [pad + i * step, h - pad - (p.count / max) * (h - 2 * pad)] as const);
  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${(w - pad).toFixed(1)},${h - pad}`;
  return (
    <div className="grid gap-1.5">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full text-accent-strong" preserveAspectRatio="none" role="img" aria-label="Weekly trend">
        <polygon points={area} fill="currentColor" fillOpacity="0.12" />
        <polyline points={line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-muted">
        <span>{points[0]?.label}</span>
        <span>{points[points.length - 1]?.label}</span>
      </div>
    </div>
  );
}
